import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { analyzeText, easeLabel, gradeLabel } from "./readability";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Analyzes a piece of writing and calculates its Flesch Reading Ease, Flesch-Kincaid, Gunning Fog, SMOG, Coleman-Liau and ARI scores, giving you an objective measure of how easy or difficult the text is to read — useful for checking that content written for a general audience isn't accidentally coming out at a graduate-reading-level of complexity, or the reverse.`,
  how: `Paste your text, and the reading level, word count, and sentence count are calculated automatically.`,
  tip: `Shorter sentences and simpler, more common words are the two biggest levers for lowering a reading-level score if your writing is coming out more complex than your intended audience — breaking one long sentence with several clauses into two or three shorter ones usually moves the score more than swapping individual words.`,
};

export default function ReadingLevelChecker() {
  const [text, setText] = useState("");
  const result = useMemo(() => analyzeText(text), [text]);
  const hasText = text.trim().length > 0;

  const { blocked } = useLogToolUsage(hasText);


  return (
    <ToolContainer>
      <Textarea
        placeholder="Paste your article, essay, or content here to check its reading level..."
        value={text}
        onChange={e => setText(e.target.value)}
        className="min-h-[220px]"
      />
      {!hasText && (
        <p className="mt-4 text-sm text-muted-foreground" role="status">Paste or type some text above to see its reading level.</p>
      )}
      {hasText && result.words < 5 && (
        <p className="mt-4 text-sm text-muted-foreground" role="status">Enter at least one full sentence (about 5 words or more) for a meaningful reading level.</p>
      )}
      {hasText && result.words >= 5 && !blocked && (
        <div className="mt-4 space-y-4">
          {result.language !== "ok" && (
            <NoticeBox className="text-sm" testId="rl-lang">
              {result.language === "non-latin"
                ? "These formulas are designed for English (and similar Latin-script text). Scores for this language would be misleading, so grade levels are hidden."
                : "This text doesn't look like English. The formulas are tuned for English, so treat these scores as unreliable."}
            </NoticeBox>
          )}
          {result.language !== "non-latin" && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary" data-testid="rl-flesch">{result.flesch.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Flesch Reading Ease · {easeLabel(result.flesch)}</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary" data-testid="rl-grade">{result.fk.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Flesch-Kincaid Grade · {gradeLabel(result.fk)}</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{result.words}</p>
                  <p className="text-xs text-muted-foreground mt-1">Words</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{result.sentences}</p>
                  <p className="text-xs text-muted-foreground mt-1">Sentences</p>
                </Card>
              </div>
              <p className="text-center text-sm font-medium" data-testid="rl-overall">
                Overall: about {gradeLabel(result.avgGrade)} reading level
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center text-sm">
                {[
                  ["Gunning Fog", result.fog],
                  ["SMOG", result.smog],
                  ["Coleman-Liau", result.cli],
                  ["ARI", result.ari],
                  ["Hard words", result.complexCount],
                ].map(([k, v]) => (
                  <div key={k as string} className="rounded-md border p-2">
                    <div className="font-semibold">
                      {v === null ? "n/a" : k === "Hard words" ? (v as number) : (v as number).toFixed(1)}
                    </div>
                    <div className="text-xs text-muted-foreground">{k as string}{v === null ? " (needs 3+ sentences)" : ""}</div>
                  </div>
                ))}
              </div>
              {result.longSentences.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-1">Long sentences (over 25 words): {result.longSentences.length}</p>
                  <ul className="space-y-1 text-sm">
                    {result.longSentences.slice(0, 8).map((s, i) => (
                      <li key={i} className="rounded bg-amber-500/10 px-2 py-1">{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.hardWords.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-1">Hard words (3+ syllables)</p>
                  <p className="text-sm text-muted-foreground">{result.hardWords.join(", ")}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
